/**
 * Search Service
 * Handles search-related API endpoints
 */

import apiClient from './api/apiClient';
import { ENDPOINTS, buildEndpoint } from './api/endpoints';
import { getSelectedLanguage, normalizeLanguageCode } from 'utils/language';

/**
 * Search Service Object
 * Contains all methods for searching materials
 */
export const searchService = {
  /**
   * Search Materials (Text or AI)
   * Endpoint: /materials/search
   *
   * @param {Object} params - Query parameters
   * @param {string} params.query - Search text (required)
   * @param {string} params.type - "text" for keyword search, "ai" for AI-powered search (default: "text")
   * @param {number} params.limit - Maximum number of results 1-100 (default: 20)
   * @param {number} params.offset - Start offset for paginated results (default: 0)
   *
   * Returns for type=text: Array<{
   *   id: number,
   *   code: string,
   *   name: string,
   *   description: string,
   *   last_value: number,
   *   value_unit: string,
   *   currency: string,
   *   last_date: string (ISO format),
   *   datapoints: Array<{
   *     ts: string (ISO format),
   *     value: number,
   *     value_unit: string,
   *     currency: string
   *   }>
   * }>
   *
   * Returns for type=ai: {
   *   results: Array (same structure as above),
   *   ai_summary: string
   * }
   */
  search: async (params = {}) => {
    const { query, type = 'text', limit = 20, offset = 0, language } = params;

    if (!query || !query.trim()) {
      throw new Error('Search query is required');
    }

    const endpoint = buildEndpoint(ENDPOINTS.MATERIALS.SEARCH, {
      query: query.trim(),
      type,
      limit,
      offset,
      lang: normalizeLanguageCode(language || getSelectedLanguage()),
    });

    return apiClient.get(endpoint);
  },

  /**
   * Text Search (convenience method)
   * Performs keyword search over material name, description, and code
   *
   * @param {string} query - Search text
   * @param {number} limit - Maximum number of results (default: 20)
   * @returns {Promise<Array>} - Array of matching materials
   */
  searchText: async (query, limit = 20, language) => {
    return searchService.search({ query, type: 'text', limit, language });
  },

  /**
   * AI Search (convenience method)
   * Performs AI-powered RAG search with LLM summarization
   *
   * @param {string} query - Search text
   * @param {number} limit - Maximum number of results (default: 20)
   * @returns {Promise<Object>} - Object with results array and ai_summary
   */
  searchAI: async (query, limit = 20, language) => {
    return searchService.search({ query, type: 'ai', limit, language });
  },
};

export default searchService;
